import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, Spin, Table, Tag,
} from 'antd';
import DraggableModal from '../../modal/DraggableModal';
import documentDefaultLabel from './autoUploadRuleDocumentDefaults';

const OUTCOME_LABELS = {
  upload: 'Would upload',
  upload_and_task: 'Would upload + task',
  skip: 'Would skip',
  skip_with_failed_row: 'Would skip (failure recorded)',
  no_match: 'No rule matches — no upload',
};

const OUTCOME_COLORS = {
  upload: 'green',
  upload_and_task: 'blue',
  skip: 'orange',
  skip_with_failed_row: 'red',
  no_match: 'default',
};

const describeDocumentDefaults = (defaults = {}) => Object.entries(defaults || {}).map(([key, value]) => {
  const label = documentDefaultLabel(key);
  return typeof value === 'boolean' ? `${label}: ${value ? 'Yes' : 'No'}` : `${label}: ${value}`;
}).join(' · ') || '—';

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

const columns = [
  {
    title: 'Document', dataIndex: 'file_name', key: 'file_name', render: (name) => name || <em>Untitled</em>,
  },
  {
    title: 'Category', dataIndex: 'emr_category', key: 'emr_category', render: (category) => category || '—',
  },
  {
    title: 'Arrived',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (createdAt) => (createdAt ? dateFormatter.format(new Date(createdAt)) : '—'),
  },
  {
    title: 'Outcome',
    dataIndex: 'outcome',
    key: 'outcome',
    render: (outcome) => (
      <Tag color={OUTCOME_COLORS[outcome] || 'default'}>{OUTCOME_LABELS[outcome] || outcome}</Tag>
    ),
  },
  {
    title: 'Matched rule',
    key: 'matched_rule',
    render: (_, row) => (row.matched_rule_id ? `${row.matched_rule_name || 'Unnamed'} (#${row.matched_rule_id})` : '—'),
  },
  {
    title: 'Defaults',
    key: 'document_defaults',
    render: (_, row) => describeDocumentDefaults(row.document_defaults),
  },
];

const AutoUploadRulesPreviewModal = ({
  visible, preview, isLoading, onClose,
}) => (
  <DraggableModal
    visible={visible}
    title="Preview against recent documents"
    onCancel={onClose}
    footer={<Button onClick={onClose}>Close</Button>}
    width={860}
    destroyOnClose
  >
    <p style={{ color: '#888' }}>
      A dry run of your current rules against recently received documents. Nothing is uploaded or changed.
    </p>
    <Spin spinning={isLoading}>
      <Table
        rowKey={(row) => `${row.receiver_type}-${row.receiver_id}`}
        size="small"
        bordered
        pagination={false}
        columns={columns}
        dataSource={preview}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: isLoading ? ' ' : 'No recent documents to test against' }}
      />
    </Spin>
  </DraggableModal>
);

AutoUploadRulesPreviewModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  preview: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AutoUploadRulesPreviewModal;
export { describeDocumentDefaults };
